// Gráfico da curva de desembolso Ampli DENTRO da tela de Desembolso.
//
// A FONTE (design D15) é a MESMA que alimenta a raiz da tabela da EAP: a soma, por
// competência, do desembolso de cada unidade (`project.units[].monthlyProjection
// .ampliDisbursement`, o que `getUnits` publica), recortada pelo MESMO período
// (`filterDates`, o do `DisbursementPeriodFilter`). As unidades são recarregadas quando o
// período muda, então barra e raiz da EAP são o mesmo número em qualquer janela — inclusive
// uma janela MAIOR que a da seleção da obra.
//
// O que foi corrigido: o gráfico lia `plannedCashFlowSeries.ampliDisbursement`, que o
// `getDetail` recorta pela janela da seleção da obra (`selectProject`) e que a troca de
// período não recarrega. Preso à janela antiga (07/2026–01/2027 no caso visto em stg), ele
// mostrava menos da curva do que a EAP (desde 05/2024). Essa série continua como plano B só
// ENQUANTO as unidades não chegaram — a primeira pintura nunca fica em branco — e nunca
// `resolveAmpliSeries`/`project.ampliDisbursements`, o recorte da Projeção Financeira que
// sobrevive à troca de tela.
//
// Só a curva Ampli é desenhada (revisão de 2026-10-04): a série da origem (antes das
// regras, `plannedCashFlowSeries.ampliDisbursementOrigin`) saiu do gráfico a pedido do
// produto — o cliente quer ver o desembolso que vale, não o que as regras moveram. O
// vínculo com a Projeção Financeira é preservado à parte, em `AmpliDisbursementCoverageCard`.
import { useEffect, useMemo } from "react";
import { useProjectActions, useProjectContext } from "@contexts/projects";

import {
  ampliCurveState,
  ampliCurveStateLabel,
  ampliInconsistentReasonLabel,
  isAmpliDisbursementPolicy,
} from "../financialProjection/cashFlowPlanning";
import MonthlyBarChart from "./MonthlyBarChart";
import monthsBetween from "./months";

const PLAIN_UNAVAILABLE_HINT =
  "Esta obra ainda não tem previsto canônico para gerar a curva.";

const STATE_DETAILS = Object.freeze({
  stale: "A curva anterior continua no ar enquanto a nova é gerada.",
  unavailable: PLAIN_UNAVAILABLE_HINT,
});

/** `{ month, year, value }` → `"MM/AAAA"`, a competência no formato das colunas da EAP. */
const competenceOf = (point) =>
  `${String(point.month).padStart(2, "0")}/${point.year}`;

/** `YYYYMM` de `"MM/AAAA"`, só para ordenar. */
const sortKey = (date) => String(date).slice(3) + String(date).slice(0, 2);

const bySortKey = (left, right) =>
  sortKey(left.date) < sortKey(right.date) ? -1 : 1;

/**
 * A fonte da raiz da EAP: o desembolso de cada unidade somado por competência. `null`
 * quando as unidades ainda não chegaram (ausente é diferente de "sem desembolso").
 */
const rootSeriesFromUnits = (units) => {
  if (!Array.isArray(units)) return null;
  const byMonth = new Map();
  units.forEach((unit) => {
    const points = unit?.monthlyProjection?.ampliDisbursement;
    if (!Array.isArray(points)) return;
    points.forEach((point) => {
      const date = competenceOf(point);
      byMonth.set(date, (byMonth.get(date) ?? 0) + (Number(point.value) || 0));
    });
  });
  return [...byMonth.entries()].map(([date, value]) => ({ date, value }));
};

const publishedSeries = (series) => (Array.isArray(series) ? series : []);

/**
 * Uma barra por mês do período escolhido, na ordem do período — não na ordem em que a
 * série chegou. Um mês sem valor na curva entra como zero: a lacuna no meio de um
 * período contínuo é informação (naquele mês não se desembolsa nada), sumir com a
 * barra encostaria meses não vizinhos e mentiria sobre o intervalo.
 *
 * Sem período legível, a série inteira, ordenada — é o que a tela mostrava antes de
 * existir seletor, e continua sendo o melhor palpite.
 */
const alignToPeriod = (series, months) => {
  if (months.length === 0) return [...series].sort(bySortKey);
  const byMonth = new Map(series.map((point) => [point.date, point]));
  return months.map((date) => ({ date, value: byMonth.get(date)?.value ?? 0 }));
};

const seriesForPeriod = (project, months) => {
  const fromUnits = rootSeriesFromUnits(project?.units);
  const resulting =
    fromUnits ?? publishedSeries(project?.plannedCashFlowSeries?.ampliDisbursement);
  return alignToPeriod(resulting, months);
};

const AmpliCurveChart = ({
  project,
  selectedMonths = null,
  onToggleMonth = null,
  onClearMonths = null,
}) => {
  const { filterDates, projectionScopeGeneration } = useProjectContext();
  const { loadUnits } = useProjectActions();
  const workId = project?._id;
  const ampli = isAmpliDisbursementPolicy(project?.cashFlowPlanning);

  // As unidades são a fonte da barra: pedidas aqui (e não só pela tabela da EAP, que nem
  // sempre está montada) e de novo a cada troca de período, que é o que mantém a barra
  // igual à raiz da EAP numa janela diferente da da seleção da obra.
  // E de novo quando a obra é recarregada (depois de salvar uma regra): o projeto novo
  // chega sem as unidades, e a barra ficaria zerada até a próxima troca de período.
  useEffect(() => {
    if (workId && ampli) loadUnits?.();
  }, [workId, ampli, filterDates, projectionScopeGeneration]);

  const months = useMemo(
    () => monthsBetween(filterDates?.beginAt, filterDates?.endAt),
    [filterDates?.beginAt, filterDates?.endAt],
  );
  const series = useMemo(
    () => seriesForPeriod(project, months),
    [project, months],
  );

  if (!isAmpliDisbursementPolicy(project?.cashFlowPlanning)) return null;

  const curve = ampliCurveState(project);
  const detail = curve.reason
    ? ampliInconsistentReasonLabel(curve.reason)
    : STATE_DETAILS[curve.status] ?? null;
  const hasSeries = series.length > 0;

  const labels = series.map((point) => point.date);
  const datasets = [
    {
      label: "Desembolso Ampli (R$)",
      data: series.map((point) => Number(point.value) || 0),
      backgroundColor: "#2563EB",
      selectable: true,
    },
  ];

  return (
    <section className={`ampli-curve-chart ampli-curve-chart--${curve.status}`}>
      <h2>Desembolso Ampli</h2>
      <p className="card-label">{ampliCurveStateLabel(curve.status)}</p>
      {detail ? <p>{detail}</p> : null}
      {hasSeries ? (
        <MonthlyBarChart
          labels={labels}
          datasets={datasets}
          showLegend={false}
          ariaLabel="Gráfico do desembolso mensal da obra"
          selectedMonths={selectedMonths}
          onToggleMonth={onToggleMonth}
          onClearMonths={onClearMonths}
        />
      ) : (
        <p className="empty-state">
          Nenhuma curva de desembolso para desenhar.
        </p>
      )}
    </section>
  );
};

export default AmpliCurveChart;
